import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import RadioGroup from '@mui/material/RadioGroup';
import type { ReactElement } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { NUMERIC_FONT } from '../../app/theme';
import { useFeatures } from '../../lib/config/features-context';

import { OptionRadio, SettingsSection } from './SettingsSection';
import type { SettingsDraft } from './settings-draft';

// The notice reads as a status message (S9 R19), as on ChannelsTabView and AccountScreen.
const STATUS_ROLE = 'status';

/**
 * "Execution mode" card (M2 R9, R10; mockup 6): automatic or confirmation, none pre-selected
 * (R2). The delay is shown read-only until Q24: the saved one, else 5 minutes, never reset (I7).
 * The follow-up reminder is always there.
 *
 * P8 (Proposal, to be validated): while `useFeatures().confirmationModeEnabled` is off, the
 * Confirmation option is not rendered at all (R1: no disabled option); Automatic stays a radio
 * the trader taps, never pre-selected (M2 R2, I7). A channel saved in Confirmation shows a
 * notice above the choice (R3) until the trader saves another mode.
 */
export function ExecutionSection({
  draft,
  onChange,
  savedModeHidden,
}: {
  readonly draft: SettingsDraft;
  readonly onChange: (patch: Partial<SettingsDraft>) => void;
  /** The saved version is in a mode the trader cannot choose now (P8 R3). */
  readonly savedModeHidden: boolean;
}): ReactElement {
  const { t } = useTranslation();
  const { confirmationModeEnabled } = useFeatures();
  return (
    <SettingsSection title={t('settings.execution.title')}>
      {savedModeHidden && (
        <Alert severity="warning" role={STATUS_ROLE}>
          {t('settings.execution.confirmationUnavailable')}
        </Alert>
      )}
      <RadioGroup
        aria-label={t('settings.execution.title')}
        value={draft.executionMode ?? ''}
        onChange={(event) => {
          const mode = event.target.value;
          if (mode === 'AUTOMATIC' || mode === 'CONFIRMATION') onChange({ executionMode: mode });
        }}
        sx={{ gap: 1.5 }}
      >
        <OptionRadio
          value="AUTOMATIC"
          title={t('settings.execution.automatic')}
          hint={t('settings.execution.automaticHint')}
        />
        {confirmationModeEnabled && (
          <OptionRadio
            value="CONFIRMATION"
            title={t('settings.execution.confirmation')}
            hint={
              <Trans
                i18nKey="settings.execution.confirmationHint"
                values={{ minutes: draft.expiryMinutes }}
                components={{ num: <Box component="span" sx={{ fontFamily: NUMERIC_FONT }} /> }}
              />
            }
          />
        )}
      </RadioGroup>
      <Box component="p" sx={{ m: 0, fontSize: 13, color: 'text.secondary', lineHeight: 1.45 }}>
        {t('settings.execution.followUps')}
      </Box>
    </SettingsSection>
  );
}
